import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  User,
  Users,
  UserPlus,
  CreditCard,
  Wallet,
  Layers,
  List,
  Image,
  Ban,
  Plus,
  Minus,
  CheckCircle,
  Trophy,
  MapPin,
  DollarSign,
  Landmark,
  ThumbsUp,
  Heart,
  FileText,
  Flag,
  Hourglass,
  Key,
  BarChart3,
  Bell,
  ChevronDown,
  TrendingUp,
  PieChart,
  Activity,
  LogOut,
  Gavel,
  ClipboardClock,
  Undo2,
  Truck,
  Forklift,
  ListTree,
  History,
  CalendarDays,
  CalendarCheck,
  IdCard,
  Award,
  ClipboardCheck,
  Settings,
  WalletMinimal,
  BanknoteArrowDown,
} from 'lucide-react';
import i18n from '../../i18n';
import { getLoggedUser } from '../../state/user';
import { getNotificationCounts } from '../../models/payments';
import { SideBarMob } from './sidebar-mob';
import { getUserInfo } from '../../models/users';
import { generateMzadcomID } from '../../state/utils';
import '../../styles/components/sidebar.scss';

const user = getLoggedUser();
const is_logged = localStorage.getItem('is_logged_in') ? true : false;
const allowed_roles = ['F', 'U', 'SA'];

function logout() {
  localStorage.clear();
  window.location.href = '/';
}

export class SideBar extends React.Component {
  constructor() {
    super();
    this.state = {
      dashboard_menu_expand: false,
      report_menu_expand: false,
      wallet_amount: 0,
      pendingMzadcomPayment: 0,
      pendingSupplierPayment: 0,
      pendingAuctionPayment: 0,
    };
    this.notificationCheck = this.notificationCheck.bind(this);
  }

  componentDidMount() {
    if (user && allowed_roles.includes(user.role)) {
      this.notificationCheck();
      this.notificationTimer = setInterval(() => {
        this.notificationCheck();
      }, 60000);
    }
  }

  componentWillUnmount() {
    if (this.notificationTimer) {
      clearInterval(this.notificationTimer);
    }
  }

  async checkUserWallet() {
    this.setState({ is_loading: true });
    const response = await getUserInfo(user.id);
    if (response && response.success) {
      this.setState({ is_loading: false, wallet_amount: response.data.wallet_amount });
    } else {
      this.setState({ is_loading: false });
    }
  }

  async notificationCheck() {
    const response = await getNotificationCounts();
    if (!response || !response.data) return;
    const data = response.data;
    this.setState({
      pendingMzadcomPayment: data.mzadcom_payment_count,
      pendingSupplierPayment: data.supplier_auction_count,
      pendingAuctionPayment: data.auction_payment_count,
    });
  }

  toggleDashboardMenu = () => {
    this.setState({ dashboard_menu_expand: !this.state.dashboard_menu_expand });
  };

  toggleReportMenu = () => {
    this.setState({ report_menu_expand: !this.state.report_menu_expand });
  };

  /**
   * Single place that decides whether a menu item is visible for the
   * current user role. `roles` = allow-list, `exclude` = deny-list.
   */
  canView = ({ roles, exclude }) => {
    if (exclude && exclude.includes(user.role)) return false;
    if (roles && !roles.includes(user.role)) return false;
    return true;
  };

  renderItem = (item) => {
    if (!this.canView(item)) return null;
    const Icon = item.icon;
    const badge = item.badge ? item.badge() : 0;
    return (
      <NavLink key={item.to} to={item.to} className="nav-item">
        <Icon className="nav-icon-svg" strokeWidth={2} />
        <span className="nav-label">{typeof item.label === 'function' ? item.label() : i18n.t(item.label)}</span>
        {badge > 0 && <span className="count-badge">{badge}</span>}
      </NavLink>
    );
  };

  renderSubItem = (item) => {
    if (!this.canView(item)) return null;
    const Icon = item.icon;
    return (
      <NavLink key={item.to} to={item.to} className="nav-subitem">
        <Icon className="nav-icon-svg" strokeWidth={2} size={16} />
        <span className="nav-label">{i18n.t(item.label)}</span>
      </NavLink>
    );
  };

  render() {
    const { wallet_amount, dashboard_menu_expand, report_menu_expand } = this.state;

    // ---- Menu definition (roles/routes identical to old SideBarNew) ----

    const dashboardSubmenu = [
      { to: '/dashboard', label: 'live_dashboard', icon: Activity },
      { to: '/dashboard-auc-eval', label: 'auction_evaluation', icon: PieChart },
      { to: '/dashboard-user-analysis', label: 'user_analysis', icon: Users },
      { to: '/dashboard-sales-eval', label: 'sales_evaluation', icon: TrendingUp },
      { to: '/cash-flow', label: 'rop_cash_flow_dashboard', icon: BarChart3 },
    ];

   

    const managementItems = [
      { to: '/users', label: 'users', icon: Users, roles: ['A', 'PM', 'OM', 'GM', 'CE', 'M', 'O', 'SA'] },
      { to: '/officers', label: 'officers', icon: IdCard, roles: ['A', 'PM', 'SA', 'M', 'OM'] },
      {
        to: '/meetings',
        label: () => `${i18n.t('schedule')} ${i18n.t('weekly')} ${i18n.t('meetings')}`,
        icon: CalendarDays,
        roles: ['RS', 'A', 'PM', 'SA', 'M', 'OM'],
      },
      {
        to: '/todays-meeting',
        label: () => `${i18n.t('todays')} ${i18n.t('meeting')}`,
        icon: CalendarCheck,
        roles: ['RS', 'A', 'SA', 'O'],
      },

       {
        to: '/auction-statistics',
        label: () => `${i18n.t('auction_statistics')}`,
        icon: BarChart3,
        roles: ['SA', 'A' , 'CE', 'GM'],
      },
      

      {
        to: '/cash-flow', label: 'rop_cash_flow_dashboard', icon: BarChart3,
        roles: ['CE', 'GM', 'F'],
      },
    ];

    const walletItems = [
      // {
      //   to: '/users-wallet',
      //   label: () => `${i18n.t('mzadcom')} - ${i18n.t('wallet')}`,
      //   icon: Wallet,
      //   roles: ['A', 'F', 'SA', 'CE', 'GM'],
      // },
      // { to: '/withdraw-requests', label: 'withdraw_requests', icon: Undo2, roles: ['A', 'F', 'SA', 'CE', 'GM'] },
      {
        to: '/rop-transactions-summary',
        label: () => `${i18n.t('mzadcom')} - ${i18n.t('transactions')}`,
        icon: Wallet,
        roles: ['A', 'F', 'CE', 'GM', 'SA'],
      },
      { to: '/recharge-requests', label: 'recharge_requests', icon: WalletMinimal, roles: ['A', 'F', 'CE', 'GM', 'SA'] },
      { to: '/withdrawal-requests', label: 'withdrawal_requests', icon: BanknoteArrowDown, roles: ['A', 'F', 'CE', 'GM', 'SA'] },
    ];

    const adminItems = [
      { to: '/groups', label: 'groups', icon: Layers, roles: ['SA', 'A'] },
      { to: '/categories', label: 'categories', icon: List, roles: ['SA', 'A'] },
      { to: '/departments', label: 'departments', icon: ListTree, roles: ['SA', 'A'] },
      { to: '/locations', label: 'locations', icon: MapPin, roles: ['SA', 'A'] },
      { to: '/sub-divisions', label: 'sub_divisions', icon: ListTree, roles: ['SA', 'A'] },
      { to: '/banners', label: 'banners', icon: Image, roles: ['SA', 'A'] },
      { to: '/blacklist', label: 'blacklist', icon: Ban, roles: ['SA', 'A'] },
      { to: '/auction/create', label: 'create_auction', icon: Plus, roles: ['SA', 'A'] },
    ];

    const auctionItems = [
      {
        to: '/auction_request',
        label: 'auction_request',
        icon: FileText,
        roles: ['C', 'RS', 'A', 'PM', 'SA', 'M', 'OM'],
      },
      {
        to: '/auctions-list',
        label: 'my_auctions',
        icon: Gavel,
        roles: ['A', 'C', 'PM', 'SA', 'OM', 'GM', 'CE', 'RS', 'M'],
      },
      {
        to: '/auction_approval',
        label: 'auction_approval',
        icon: ClipboardClock,
        roles: ['A', 'PM', 'OM', 'SA', 'F'],
      },
      {
        to: '/winners_approval',
        label: 'winners_approval',
        icon: Award,
        roles: ['A', 'PM', 'OM', 'SA', 'C'],
      },
      { to: '/approved_auctions', label: 'approved_auctions', icon: CheckCircle, roles: ['SA', 'A'] },
      {
        to: '/auction-status',
        label: 'auction_report',
        icon: ClipboardCheck,
        roles: ['A', 'HSE', 'F', 'SA', 'PM'],
      },
    ];

    const bidderItems = [
      { to: '/my_auctions', label: 'my_bids', icon: Key, roles: ['U'] },
      { to: '/enrolled_auctions', label: 'enrolled_auctions', icon: FileText, roles: ['U'] },
      { to: '/winning_bids', label: 'winning_bids', icon: Trophy, roles: ['U'] },
      {
        to: '/rop-payment',
        label: 'rop_payment',
        icon: CreditCard,
        roles: ['U'],
        badge: () => this.state.pendingMzadcomPayment,
      },
      { to: '/wallet', label: 'my_wallet', icon: Wallet, roles: ['U'] },
      { to: '/auction_liked', label: 'liked_auctions', icon: ThumbsUp, roles: ['U'] },
      { to: '/auction_wishlisted', label: 'wishlisted_auctions', icon: Heart, roles: ['U'] },
    ];

    const siteItems = [
      { to: '/loading-management', label: 'loading_management', icon: Forklift, roles: ['HSE', 'PM', 'O'] },
      { to: '/vehicles', label: 'vehicles', icon: Truck, roles: ['HSE', 'PM', 'O'] },
      { to: '/delivery-site-list', label: 'delivery_site_list', icon: List, roles: ['HSE', 'PM', 'O'] },
      { to: '/weighbridge-ticket-list', label: 'weighbridge_ticket_list', icon: List, roles: ['HSE', 'PM', 'O'] },
      { to: '/site-visit-projects', label: 'site_visit_projects', icon: MapPin, roles: ['HSE', 'PM', 'O'] },
      { to: '/site-visit-requests', label: 'site_visit_req', icon: MapPin, roles: ['HSE', 'PM', 'O'] },
      { to: '/site_visit', label: 'site_visit', icon: MapPin, exclude: ['O'] },
    ];

    const paymentItems = [
      {
        to: '/auction-payment',
        label: 'auction_payments',
        icon: CreditCard,
        roles: ['F', 'SA'],
        badge: () => this.state.pendingAuctionPayment,
      },
      { to: '/payments', label: 'payment', icon: CreditCard, roles: ['A', 'SA', 'C', 'M'] },
      { to: '/client-payments', label: 'client_payment', icon: Landmark, roles: ['A', 'PM', 'SA', 'OM'] },
      {
        to: '/refund',
        label: 'refund',
        icon: Undo2,
        roles: ['A', 'PM', 'SA', 'F', 'OM', 'GM', 'CE'],
      },
      {
        to: '/enrolled_users',
        label: 'enrolled_users',
        icon: UserPlus,
        roles: ['A', 'C', 'PM', 'OM', 'GM', 'CE', 'F', 'SA', 'M'],
      },
    ];

    const reportRoles = ['A', 'C', 'PM', 'OM', 'GM', 'CE', 'F', 'SA', 'M'];
    const reportItems = [
      { to: '/final_report', label: 'final_report', icon: Hourglass, roles: ['RS'] },
      {
        to: '/auction_bid_report',
        label: 'auction_bid_report',
        icon: FileText,
        roles: ['A', 'PM', 'OM', 'GM', 'CE', 'SA', 'M'],
      },
      {
        to: '/sales_report',
        label: 'sales_report',
        icon: DollarSign,
        roles: ['A', 'PM', 'OM', 'GM', 'CE', 'SA', 'M'],
      },
      { to: '/winners', label: 'auction_winners', icon: Flag, roles: ['A', 'PM', 'OM', 'GM', 'CE', 'SA', 'M'] },
      {
        to: '/final_report',
        label: 'final_report',
        icon: Hourglass,
        roles: ['A', 'PM', 'OM', 'GM', 'CE', 'SA', 'M'],
      },
      {
        to: '/position_report',
        label: 'position_report',
        icon: Key,
        roles: ['A', 'PM', 'OM', 'GM', 'CE', 'SA', 'M'],
      },
      {
        to: '/wallet_summary',
        label: 'wallet_summary',
        icon: Wallet,
        roles: ['A', 'PM', 'OM', 'GM', 'CE', 'SA', 'M'],
      },
      { to: '/reports', label: 'reports', icon: BarChart3 },
    ];

    const bottomItems = [
      { to: '/composer', label: 'composer', icon: Bell, roles: ['A', 'PM', 'SA', 'OM', 'GM', 'CE', 'M'] },
      { to: '/system-settings', label: 'system_settings', icon: Settings, roles: ['A', 'PM', 'SA', 'GM'] },
    ];

    return (
      <React.Fragment>
        {is_logged && (
          <div>
            <SideBarMob wallet_amount={wallet_amount} />
            <div className="d-mob-none">
              <div className="professional-sidebar">
                {/* Dashboard Header */}
                <div className="sidebar-dashboard-header">
                  <LayoutDashboard className="dashboard-icon" size={20} />
                  <span className="dashboard-title">{i18n.t('dashboard')}</span>
                </div>

                {/* User Profile Card */}
                <UserProfileCard user={user} i18n={i18n} generateMzadcomID={generateMzadcomID} />

                {/* Navigation Menu */}
                <nav className="sidebar-navigation">
                  {/* Dashboard: flat link for simple roles, submenu for admin-type roles */}
                  {['U', 'CE', 'GM', 'C', 'RS', 'O'].includes(user.role) ? (
                    this.renderItem({ to: '/dashboard', label: 'dashboard', icon: LayoutDashboard })
                  ) : (
                    <div className="nav-group">
                      <button className="nav-item nav-expandable" onClick={this.toggleDashboardMenu}>
                        <LayoutDashboard className="nav-icon-svg" strokeWidth={2} />
                        <span className="nav-label">{i18n.t('dashboard')}</span>
                        <ChevronDown className={`expand-icon ${dashboard_menu_expand ? 'expanded' : ''}`} size={16} />
                      </button>
                      <div className={`nav-submenu ${dashboard_menu_expand ? 'show' : ''}`}>
                        {dashboardSubmenu.map(this.renderSubItem)}
                      </div>
                    </div>
                  )}

                  {/* Profile */}
                  {this.renderItem({ to: '/profile', label: 'edit_profile', icon: User })}

                  {/* User & org management */}
                  {managementItems.map(this.renderItem)}

                  {/* Wallet management */}
                  {walletItems.map(this.renderItem)}

                  {/* Admin masters */}
                  {adminItems.map(this.renderItem)}

                  {/* Auctions */}
                  {auctionItems.map(this.renderItem)}

                  {/* Bidder-only */}
                  {bidderItems.map(this.renderItem)}

                  {/* Site / logistics */}
                  {siteItems.map(this.renderItem)}

                  {/* Payments */}
                  {paymentItems.map(this.renderItem)}

                  {/* Track auctions (all roles) */}
                  {this.renderItem({ to: '/auction_tracker', label: 'track_auctions', icon: History })}

                  {/* Reports group */}
                  {this.canView({ roles: [...reportRoles, 'RS'] }) && (
                    <div className="nav-group">
                      <button className="nav-item nav-expandable" onClick={this.toggleReportMenu}>
                        {report_menu_expand ? (
                          <Minus className="nav-icon-svg" strokeWidth={2} />
                        ) : (
                          <Plus className="nav-icon-svg" strokeWidth={2} />
                        )}
                        <span className="nav-label">{i18n.t('reports')}</span>
                        <ChevronDown className={`expand-icon ${report_menu_expand ? 'expanded' : ''}`} size={16} />
                      </button>
                      <div className={`nav-submenu ${report_menu_expand ? 'show' : ''}`}>
                        {reportItems.map(this.renderSubItem)}
                      </div>
                    </div>
                  )}

                  {/* Composer + Settings */}
                  {bottomItems.map(this.renderItem)}

                  {/* Logout */}
                  <button onClick={logout} className="nav-item nav-logout">
                    <LogOut className="nav-icon-svg" strokeWidth={2} />
                    <span className="nav-label">{i18n.t('logout')}</span>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </React.Fragment>
    );
  }
}

// Simple User Profile Card Component with SVG Icons
const UserProfileCard = ({ user, i18n, generateMzadcomID }) => {
  return (
    <div className="sidebar-profile-card">
      <div className="profile-header-section">
        <div className="profile-avatar-wrapper">
          {user.avatar ? (
            <img src={user.avatar} alt={user.name} className="profile-avatar-image" referrerPolicy="no-referrer" />
          ) : (
            <div className="profile-avatar-placeholder">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
              </svg>
            </div>
          )}
        </div>
        <div className="profile-info-wrapper">
          <div className="profile-user-name">{user.name}</div>
          <div className="profile-user-email">{user.email}</div>
        </div>
      </div>

      <div className="profile-divider"></div>

      <div className="profile-details-grid">
        <div className="profile-detail-item">
          <svg viewBox="0 0 24 24" fill="currentColor" className="detail-icon">
            <path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z" />
          </svg>
          <div className="detail-content">
            <span className="detail-label">{i18n.t('mobile')}</span>
            <span className="detail-value">{user.mobile}</span>
          </div>
        </div>

        <div className="profile-detail-item">
          <svg viewBox="0 0 24 24" fill="currentColor" className="detail-icon">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
          </svg>
          <div className="detail-content">
            <span className="detail-label">{i18n.t('mzadcom_id')}</span>
            <span className="detail-value">{generateMzadcomID(user.id, user.role)}</span>
          </div>
        </div>

        <div className="profile-detail-item">
          <svg viewBox="0 0 24 24" fill="currentColor" className="detail-icon">
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
          </svg>
          <div className="detail-content">
            <span className="detail-label">{i18n.t('email')}</span>
            <span className="detail-value">{user.email}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileCard;